import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { GroupedBillingListResult } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE } from "../invoices/labels";
import { groupedOutputs, groupedState } from "./labels";

/** Finanças › Faturamento agrupado (RF-FIN-03): agrupamentos por número, cliente, período e situação. */
export function GroupedBillingListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "grouped_billing", "create");
  const list = useInfiniteQuery({
    queryKey: ["grouped-billing", companyId, "list", term, state, from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<GroupedBillingListResult>(
        `/grouped-billing?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(state ? { state } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const create = () => navigate({ to: "/financas/faturamento-agrupado/novo" });

  return (
    <>
      <ContentHeader
        title="Faturamento agrupado"
        breadcrumb={["Finanças", "Faturamento agrupado"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Faturar pedidos
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="gb-q">
              Pesquisar faturamento
            </label>
            <input
              id="gb-q"
              type="search"
              className="form-control"
              placeholder="Número ou cliente"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="gb-state">
              Situação
            </label>
            <select
              id="gb-state"
              className="form-select"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="active">Em vigor</option>
              <option value="reversed">Desfeitos</option>
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="gb-from">
              Data inicial
            </label>
            <input
              id="gb-from"
              type="date"
              className="form-control"
              title="Data inicial"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="gb-to">
              Data final
            </label>
            <input
              id="gb-to"
              type="date"
              className="form-control"
              title="Data final"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum faturamento agrupado"
            description="Junte pedidos atendidos do mesmo cliente numa só NF-e e/ou num só conjunto de contas a receber."
            actions={canCreate ? [{ label: "Faturar pedidos", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Faturamentos agrupados">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Data</th>
                    <th scope="col">Cliente</th>
                    <th scope="col" className="text-end">
                      Pedidos
                    </th>
                    <th scope="col">Gerou</th>
                    <th scope="col">NF-e</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = groupedState(r);
                    return (
                      <tr key={r.id}>
                        <td>
                          <Link to="/financas/faturamento-agrupado/$id" params={{ id: r.id }}>
                            {r.number}
                          </Link>
                        </td>
                        <td>{formatDateBR(r.date)}</td>
                        <td>{r.contactName}</td>
                        <td className="text-end">{r.ordersCount}</td>
                        <td>{groupedOutputs(r)}</td>
                        <td>
                          {r.invoice ? (
                            <StatusBadge tone={INVOICE_STATUS_TONE[r.invoice.status]}>
                              {r.invoice.number ? `nº ${r.invoice.number} · ` : ""}
                              {INVOICE_STATUS_LABEL[r.invoice.status]}
                            </StatusBadge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="text-end">{formatBRL(r.totalCents)}</td>
                        <td>
                          <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && summary.count >= 0 && (
                <span className="text-muted small" data-testid="gb-summary">
                  {summary.count} {summary.count === 1 ? "faturamento" : "faturamentos"}
                  {summary.totalCents !== null && ` · total ${formatBRL(summary.totalCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
